import { useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download, Mail, RefreshCw, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { getLegalEntityLabel, type LegalEntity } from '@shared/types';
import { reconciliationApi } from '@/api/reconciliation';
import { clientDetailApi } from '@/api/client-detail';
import { BalanceVisualization, type UnpaidDocument } from './components/BalanceVisualization';
import { EmailDialog } from './components/EmailDialog';
import { DebtEmailDialog } from './components/DebtEmailDialog';
import { cn } from '@/lib/utils';

function getDefaultPeriod(): { from: string; to: string } {
  const now = new Date();
  // First day of current month
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  // 9 months back from first of month
  const from = new Date(firstOfMonth);
  from.setMonth(from.getMonth() - 9);

  // Format as YYYY-MM-DD without timezone issues
  const formatDate = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    from: formatDate(from),
    to: formatDate(now),
  };
}

export function BalanceDetailPage() {
  const { clientId, legalEntity } = useParams<{ clientId: string; legalEntity: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultPeriod = useMemo(() => getDefaultPeriod(), []);
  const [dateFrom, setDateFrom] = useState(
    searchParams.get('from') || defaultPeriod.from
  );
  const [dateTo, setDateTo] = useState(
    searchParams.get('to') || defaultPeriod.to
  );
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [debtEmailDialogOpen, setDebtEmailDialogOpen] = useState(false);
  const [debtEmailData, setDebtEmailData] = useState<{
    unpaidDocuments: UnpaidDocument[];
    totalDebt: number;
  } | null>(null);
  // PDF viewer state
  const [pdfViewerData, setPdfViewerData] = useState<{ url: string; title: string } | null>(null);

  const clientIdNum = clientId ? parseInt(clientId, 10) : 0;
  const legalEntityKey = legalEntity as LegalEntity;

  // Load client info
  const { data: clientDetail } = useQuery({
    queryKey: ['client-detail', clientIdNum],
    queryFn: () => clientDetailApi.getMobileDetail(clientIdNum),
    enabled: clientIdNum > 0,
  });

  // Load available signers for legalEntity - get highest priority (first in list)
  const { data: signers } = useQuery({
    queryKey: ['signers', legalEntityKey, dateTo],
    queryFn: () => reconciliationApi.getAvailableSigners(legalEntityKey, dateTo),
    enabled: !!legalEntityKey,
  });

  // Use highest priority signer (first in the sorted list)
  const signerId = signers?.[0]?.id;

  // Load reconciliation data
  const {
    data: reconciliationData,
    isLoading,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['reconciliation', clientIdNum, legalEntityKey, dateFrom, dateTo],
    queryFn: () => reconciliationApi.getData({
      clientId: clientIdNum,
      legalEntity: legalEntityKey,
      dateFrom,
      dateTo,
    }),
    enabled: clientIdNum > 0 && !!legalEntityKey,
  });

  // Load HTML preview with signer
  const { data: htmlPreview, isLoading: htmlLoading } = useQuery({
    queryKey: ['reconciliation-html', clientIdNum, legalEntityKey, dateFrom, dateTo, signerId],
    queryFn: () => reconciliationApi.getHtml({
      clientId: clientIdNum,
      legalEntity: legalEntityKey,
      dateFrom,
      dateTo,
      signerId,
    }),
    enabled: clientIdNum > 0 && !!legalEntityKey,
  });

  const handleDateChange = (type: 'from' | 'to', value: string) => {
    if (type === 'from') {
      setDateFrom(value);
    } else {
      setDateTo(value);
    }
    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    newParams.set(type, value);
    setSearchParams(newParams, { replace: true });
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleDownloadPdf = async () => {
    try {
      const blob = await reconciliationApi.getPdf({
        clientId: clientIdNum,
        legalEntity: legalEntityKey,
        dateFrom,
        dateTo,
        signerId,
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Акт_сверки_${clientDetail?.client.name || 'клиент'}_${dateFrom}_${dateTo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('PDF скачан');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Ошибка скачивания PDF');
    }
  };

  // Download all unpaid documents as PDFs
  const handleDownloadUnpaid = async (unpaidDocs: UnpaidDocument[]) => {
    if (unpaidDocs.length === 0) return;

    toast.info(`Скачивание ${unpaidDocs.length} документов...`);

    for (const doc of unpaidDocs) {
      try {
        const blob = doc.isPending
          ? await reconciliationApi.getInvoicePdf(doc.invoiceId, signerId)
          : await reconciliationApi.getActPdf(doc.actId!, signerId);

        const docType = doc.isPending ? 'Счёт' : 'Акт';
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${docType}_${doc.invoiceNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error(`Error downloading ${doc.invoiceNumber}:`, error);
        toast.error(`Ошибка скачивания ${doc.invoiceNumber}`);
      }
    }
  };

  // Open debt email dialog
  const handleSendDebtEmail = (unpaidDocs: UnpaidDocument[], totalDebt: number) => {
    setDebtEmailData({ unpaidDocuments: unpaidDocs, totalDebt });
    setDebtEmailDialogOpen(true);
  };

  // Preview document (invoice or act)
  const handlePreviewDocument = async (doc: UnpaidDocument) => {
    try {
      const blob = doc.isPending
        ? await reconciliationApi.getInvoicePdf(doc.invoiceId, signerId)
        : await reconciliationApi.getActPdf(doc.actId!, signerId);

      const url = URL.createObjectURL(blob);
      const docType = doc.isPending ? 'Счёт' : 'Акт';
      setPdfViewerData({ url, title: `${docType} №${doc.invoiceNumber}` });
    } catch (error) {
      console.error('Error previewing document:', error);
      toast.error('Ошибка открытия документа');
    }
  };

  // Close PDF viewer
  const closePdfViewer = () => {
    if (pdfViewerData) {
      URL.revokeObjectURL(pdfViewerData.url);
      setPdfViewerData(null);
    }
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b bg-card">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/visits/client/${clientId}`)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold truncate">
            Баланс: {getLegalEntityLabel(legalEntityKey, 'full')}
          </h1>
          {clientDetail && (
            <p className="text-sm text-muted-foreground truncate">
              {clientDetail.client.name}
            </p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Period selector */}
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => handleDateChange('from', e.target.value)}
                className="flex-1 px-2 py-1.5 text-sm border rounded bg-background"
              />
              <span className="text-muted-foreground">—</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => handleDateChange('to', e.target.value)}
                className="flex-1 px-2 py-1.5 text-sm border rounded bg-background"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefresh}
                disabled={isFetching}
              >
                <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Balance visualization */}
        {reconciliationData && reconciliationData.operations.length > 0 && (
          <BalanceVisualization
            data={reconciliationData}
            signerId={signerId}
            onDownloadUnpaid={handleDownloadUnpaid}
            onSendDebtEmail={handleSendDebtEmail}
            onPreviewDocument={handlePreviewDocument}
          />
        )}

        {/* Reconciliation HTML preview */}
        <Card>
          <CardContent className="p-0">
            <div className="flex items-center justify-between p-3 border-b">
              <div className="flex items-center gap-2 text-sm font-medium">
                <FileText className="h-4 w-4" />
                Акт сверки
              </div>
            </div>
            {htmlLoading ? (
              <div className="p-4">
                <Skeleton className="h-48 w-full" />
              </div>
            ) : htmlPreview ? (
              <div
                className="p-2 overflow-x-auto text-xs bg-white"
                style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                }}
              >
                <iframe
                  srcDoc={htmlPreview}
                  className="w-full min-h-[350px] border-0 bg-white"
                  title="Акт сверки"
                  sandbox="allow-same-origin"
                  style={{
                    transform: 'scale(0.7)',
                    transformOrigin: 'top left',
                    width: '143%',
                    height: '500px',
                  }}
                />
              </div>
            ) : (
              <div className="p-4 text-center text-sm text-muted-foreground">
                Нет данных для отображения
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Action buttons - fixed at bottom */}
      <div className="p-4 border-t bg-card space-y-2">
        <Button
          variant="email"
          className="w-full"
          onClick={() => setEmailDialogOpen(true)}
          disabled={isLoading}
        >
          <Mail className="h-4 w-4" />
          Отправить на email
        </Button>
        <Button
          variant="download"
          className="w-full"
          onClick={handleDownloadPdf}
          disabled={isLoading}
        >
          <Download className="h-4 w-4" />
          Скачать PDF
        </Button>
      </div>

      {/* Email dialog for reconciliation act */}
      <EmailDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        clientId={clientIdNum}
        clientName={clientDetail?.client.name || ''}
        legalEntity={legalEntityKey}
        dateFrom={dateFrom}
        dateTo={dateTo}
        signerId={signerId}
      />

      {/* Debt email dialog for unpaid documents */}
      {debtEmailData && (
        <DebtEmailDialog
          open={debtEmailDialogOpen}
          onOpenChange={setDebtEmailDialogOpen}
          clientLegalName={clientDetail?.client.legalName || clientDetail?.client.name || ''}
          legalEntity={legalEntityKey}
          signerId={signerId}
          unpaidDocuments={debtEmailData.unpaidDocuments}
          totalDebt={debtEmailData.totalDebt}
        />
      )}

      {/* Fullscreen PDF viewer */}
      {pdfViewerData && (
        <div className="fixed inset-0 z-50 bg-background flex flex-col">
          {/* Header */}
          <div className="flex items-center gap-3 p-3 border-b bg-card shrink-0">
            <Button variant="ghost" size="icon" onClick={closePdfViewer}>
              <X className="h-5 w-5" />
            </Button>
            <span className="font-medium truncate flex-1">{pdfViewerData.title}</span>
          </div>
          {/* PDF embed */}
          <div className="flex-1 overflow-hidden bg-white">
            <embed
              src={pdfViewerData.url}
              type="application/pdf"
              className="w-full h-full"
            />
          </div>
        </div>
      )}
    </div>
  );
}
